import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowsClockwise, FilmSlate, Camera, Sparkle, PaintBrush, CheckCircle, Image } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enhancementStyles = [
  { id: null, label: 'No Enhancement', icon: Image, description: 'Regenerate with original prompts', color: '#A1A1AA' },
  { id: 'cinematic', label: 'Cinematic', icon: FilmSlate, description: 'Film-like dramatic lighting, shallow depth of field', color: '#FF5D01' },
  { id: 'realistic', label: 'Realistic', icon: Camera, description: 'Natural lighting, documentary style', color: '#10B981' },
  { id: 'dramatic', label: 'Dramatic', icon: Sparkle, description: 'High contrast, intense shadows', color: '#8B5CF6' },
  { id: 'artistic', label: 'Artistic', icon: PaintBrush, description: 'Painterly quality, fine art aesthetic', color: '#F59E0B' },
];

export const BatchRegenerateModal = ({ open, onClose, projectId, scenes, onRegenerated }) => {
  const [selectedScenes, setSelectedScenes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const scenesWithImages = scenes.filter(s => s.asset_url);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedScenes(scenesWithImages.map(s => s.id));
    } else {
      setSelectedScenes([]);
    }
  };

  const handleSelectScene = (sceneId, checked) => {
    if (checked) {
      setSelectedScenes(prev => [...prev, sceneId]);
    } else {
      setSelectedScenes(prev => prev.filter(id => id !== sceneId));
      setSelectAll(false);
    }
  };

  const handleRegenerate = async () => {
    if (selectedScenes.length === 0) {
      toast.error('Select at least one scene to regenerate');
      return;
    }

    const confirmed = window.confirm(
      `Regenerate ${selectedScenes.length} image(s)${selectedStyle ? ` with ${selectedStyle} style` : ''}?\n\nThis will replace existing images and may take ${Math.ceil(selectedScenes.length * 17 / 60)} minutes.`
    );
    if (!confirmed) return;

    setRegenerating(true);
    setProgress({ current: 0, total: selectedScenes.length });

    try {
      // Regenerate one by one for progress tracking
      let regenerated = 0;
      let failed = 0;

      for (let i = 0; i < selectedScenes.length; i++) {
        const sceneId = selectedScenes[i];
        const scene = scenes.find(s => s.id === sceneId);
        
        setProgress({ current: i + 1, total: selectedScenes.length });
        toast.info(`Regenerating scene ${scene?.scene_number || i + 1}... (${i + 1}/${selectedScenes.length})`, { id: 'regen-progress' });

        try {
          const res = await axios.post(`${API}/projects/${projectId}/batch-regenerate`, {
            scene_ids: [sceneId],
            enhancement_style: selectedStyle,
            regenerate_all: true
          });

          if (res.data.regenerated > 0) {
            regenerated++;
          } else {
            failed++;
          }
        } catch (err) {
          failed++;
          console.error(`Failed to regenerate scene ${sceneId}:`, err);
        }
      }

      toast.success(`Regenerated ${regenerated} image(s)${failed > 0 ? `, ${failed} failed` : ''}`, { id: 'regen-progress' });
      
      if (onRegenerated) {
        onRegenerated();
      }
      
      // Reset and close
      setSelectedScenes([]);
      setSelectAll(false);
      onClose();

    } catch (err) {
      toast.error('Batch regeneration failed');
      console.error(err);
    } finally {
      setRegenerating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ArrowsClockwise size={24} weight="fill" className="text-[#FF5D01]" />
            Batch Regenerate Images
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Style Selection */}
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3 block">
              Enhancement Style (Optional)
            </Label>
            <div className="grid grid-cols-5 gap-2">
              {enhancementStyles.map((style) => {
                const Icon = style.icon;
                const isSelected = selectedStyle === style.id;
                
                return (
                  <button
                    key={style.id || 'none'}
                    onClick={() => setSelectedStyle(style.id)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      isSelected 
                        ? 'border-[#FF5D01] bg-[#FF5D01]/10' 
                        : 'border-[#333333] bg-[#0a0a0a] hover:border-[#444]'
                    }`}
                    data-testid={`style-${style.id || 'none'}-btn`}
                  >
                    <Icon 
                      size={24} 
                      weight="fill" 
                      className="mx-auto mb-1" 
                      style={{ color: isSelected ? style.color : '#666' }}
                    />
                    <div className="text-xs font-medium" style={{ color: isSelected ? style.color : '#A1A1AA' }}>
                      {style.label}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedStyle && (
              <p className="text-xs text-[#A1A1AA] mt-2">
                {enhancementStyles.find(s => s.id === selectedStyle)?.description}
              </p>
            )}
          </div>

          {/* Scene Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                Select Scenes to Regenerate
              </Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                  className="border-[#333333] data-[state=checked]:bg-[#FF5D01] data-[state=checked]:border-[#FF5D01]"
                />
                <label htmlFor="select-all" className="text-xs text-[#A1A1AA] cursor-pointer">
                  Select All ({scenesWithImages.length})
                </label>
              </div>
            </div>

            {scenesWithImages.length === 0 ? (
              <div className="text-center py-8 text-[#A1A1AA]">
                <Image size={40} className="mx-auto mb-2 opacity-50" />
                <p>No scenes with images to regenerate</p>
                <p className="text-xs">Generate images first using "Generate All"</p>
              </div>
            ) : (
              <ScrollArea className="h-[250px] border border-[#333333] rounded-lg">
                <div className="p-2 space-y-1">
                  {scenesWithImages.map((scene) => {
                    const isSelected = selectedScenes.includes(scene.id);
                    
                    return (
                      <div
                        key={scene.id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${
                          isSelected ? 'bg-[#FF5D01]/10' : 'hover:bg-[#262626]'
                        }`}
                        onClick={() => handleSelectScene(scene.id, !isSelected)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectScene(scene.id, checked)}
                          className="border-[#333333] data-[state=checked]:bg-[#FF5D01] data-[state=checked]:border-[#FF5D01]"
                        />
                        
                        {/* Thumbnail */}
                        <div className="w-12 h-8 bg-[#262626] rounded overflow-hidden flex-shrink-0">
                          {scene.asset_url && (
                            <img 
                              src={scene.asset_url} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#FF5D01]">#{scene.scene_number}</span>
                            <span className="text-xs text-[#666] px-1.5 py-0.5 bg-[#262626] rounded">
                              {scene.aspect_ratio || '16:9'}
                            </span>
                          </div>
                          <p className="text-xs text-[#A1A1AA] truncate">{scene.summary}</p>
                        </div>
                        
                        {isSelected && (
                          <CheckCircle size={16} weight="fill" className="text-[#FF5D01] flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Progress */}
          {regenerating && (
            <div className="p-3 bg-[#0a0a0a] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Regenerating...</span>
                <span className="text-sm text-[#FF5D01]">{progress.current}/{progress.total}</span>
              </div>
              <div className="w-full h-2 bg-[#333333] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#FF5D01] rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="ghost" 
            onClick={onClose} 
            disabled={regenerating}
            className="text-[#A1A1AA] hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={regenerating || selectedScenes.length === 0}
            className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
            data-testid="start-regenerate-btn"
          >
            {regenerating ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowsClockwise size={18} weight="bold" />
            )}
            {regenerating 
              ? `Regenerating ${progress.current}/${progress.total}...` 
              : `Regenerate ${selectedScenes.length} Image(s)`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
